import React, {useState, useRef, useMemo} from "react";
import {Box, ResponsiveContext, Text, Heading } from "grommet";
import {formatCurrency} from "../../../../utils/numbers";
import DataTableWithBackendSearch from "../../../DataTableWithBackendSearch";
import { getQueryString } from "../../../../utils/search";
import { handleErrors, handleFileDownloadErrors } from "../../../../utils/errors";
import { useContext } from "react";
import CustomerTag from "../../../CustomerTag";
import { useRouter } from "next/router";
import {
    IconPlus,
    IconHelpCircle as IconQuestion,
    IconCircleCheck,
    IconCircleMinus
} from '@tabler/icons-react';
import FontAwesomeIcon from "../../../FontAwesomeIcon";
import { useSWRConfig } from "swr";
import CustomerGroupFormDrawer from "../CustomerGroupFormDrawer";
import dynamic from "next/dynamic";
import { DisplayInlineBlockBox, EaseInOnHoverBox, EnhancedBox, PositionRelativeBox } from "../../../../utils/styled";
import Loading from "../../../Loading";
import { formatCityStateCountry, isEmailOnlyAddress } from "../../../../utils/addresses";
import { NotificationsContext } from "../../../NotificationsProvider";
import { formatDistance } from "date-fns";
import { sentencecase } from "../../../../utils/string";
import Button from "../../../Button";
import DropButton from "../../../DropButton";
import {useSession} from "next-auth/react";

const SendCSVModal = dynamic(() => import('../../../SendCSVModal'));
const SelectAddressModal = dynamic(() => import('../../draft-invoices/edit/SelectAddressModal'));
const EmptyPageMessage = dynamic(() => import('../EmptyPageMessage'));
const EmptyCustomerPageMessage = dynamic(() => import('../EmptyPageMessage/EmptyCustomerPageMessage'));
const CustomerListSelectedControls = dynamic(() => import('./CustomerListSelectedControls'));

const CustomerList = ({
    title,
    dataTableWrapperProps,
    isGroup,
    dataName,
    totalCount,
    ...rest
}) => {
    const {data: session} = useSession();
    const {company: { currency }} = session || {};
    const [showAddGroupDrawer, setShowAddGroupDrawer] = useState(false);
    const [selectedCustomers, setSelectedCustomers] = useState([]);
    const [showSendCSVModal, setShowSendCSVModal] = useState(false);
    const [showHowToSetUpQuickPay, setShowHowToSetupQuickPay] = useState(false);
    const [showSelectAddressModal, setShowSelectAddessModal] = useState(false);
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [updateAddressCustomerId, setUpdateAddressCustomerId] = useState();
    const [dataTableQueryUrl, setDataTableQueryUrl] = useState();
    const router = useRouter();
    const { mutate } = useSWRConfig();
    const resetSelectedRowsRef = useRef(null);
    const size = useContext(ResponsiveContext);
    const isMobile = size === "small";

    const notificationCtx = useContext(NotificationsContext);
    const [hasNoData, setHasNoData] = useState(false);

    const columns = useMemo(() => {
        if (isMobile) return [
            {
                selector: row => row.id,
                cell: (datum) => {
                    return (
                        <Box data-tag="allowRowEvents" fill="horizontal" justify="between" direction="row" gap="small" align="center" pad={{right: 'xsmall'}}>
                            <Box data-tag="allowRowEvents">
                                {isGroup ? datum?.company_name : <CustomerTag data-tag="allowRowEvents" uuid={datum?.uuid} inline />}
                            </Box>
                        </Box>
                    )
                }
            }
        ];
        return isGroup ? [
            {
                selector: row => row.company_name,
                name: 'Group name',
                sortable: true,
                sortField: 'company_name',
                cell: (datum) => datum.company_name,
            },
            {
                selector: row => row.number_of_customers,
                name: 'Members',
                sortable: true,
                right: true,
                sortField: 'number_of_customers',
                cell: (datum) => datum.number_of_customers,
            },
            {
                selector: row => row.last_invoiced_at,
                name: (<Text size="xsmall" textAlign="end">Last invoiced</Text>),
                sortable: true,
                sortField: 'last_invoiced_at',
                right: true,
                cell: (datum) => datum?.last_invoiced_at ? (<Text textAlign="end">{sentencecase(formatDistance(new Date(datum?.last_invoiced_at * 1000 ), new Date()).replace('about ', ''))} ago</Text>) : ''
            },
            {
                selector: row => row.total_open_balance_amount,
                name: (
                    <Text size="xsmall" textAlign="end">Open balance</Text>
                ),
                sortable: true,
                sortField: 'total_open_balance_amount',
                right: true,
                cell: (datum) => formatCurrency(datum?.total_open_balance_amount, datum?.currency)
            },
            {
                selector: row => row.invoice_count,
                name: (
                    <Text size="xsmall" textAlign="end">Invoices sent</Text>
                ),
                sortable: true,
                sortField: 'invoice_count',
                right: true,
                cell: (datum) => datum?.invoice_count || ''
            },
        ] : [
            {
                selector: row => row.company_name,
                name: 'Company',
                sortable: true,
                sortField: 'company_name',
                grow: 2,
                cell: (datum) => <CustomerTag uuid={datum?.uuid} inline />
            },
            {
                selector: row => row.company_name,
                name: (
                    <Box direction="row" gap="xsmall">
                        <Text size="xsmall" textAlign="start">
                            Preferred billing address
                        </Text>
                        <DropButton
                            event={{section: 'customer_list', element: 'preferred_address_help_text'}}
                            plain
                            hoverIndicator
                            dropAlign={{center: 'center', top: 'bottom'}}
                            dropContent={(
                                <Box gap="small" border={{color: "accent"}} width={{max: 'medium'}} pad="small">
                                    <Text>
                                        Set a preferred billing address to make sure your invoices get to the right person.
                                    </Text>
                                </Box>
                            )}
                            label={<FontAwesomeIcon SelectedIcon={IconQuestion} color="status-info" size="xsmall" />} />
                    </Box>
                ),
                grow: 3,
                cell: (datum) => datum?.billing_address ?
                    (
                        <Box>
                            {datum?.billing_address ?
                                (<Box direction="row" gap="xsmall">
                                    <Text truncate>
                                        {isEmailOnlyAddress(datum?.billing_address) ?
                                            datum?.billing_address?.email :
                                            formatCityStateCountry(datum?.billing_address)
                                        }
                                    </Text>
                                </Box>) : ''}
                            {!isEmailOnlyAddress(datum?.billing_address) &&
                                (<Text
                                    color="text-weak"
                                    truncate
                                    size={isEmailOnlyAddress(datum?.billing_address) ? undefined : "small"}
                                >{datum?.billing_address?.email}</Text>)}
                        </Box>
                    ) : (
                        <EaseInOnHoverBox styles="width: 100%;">
                            <PositionRelativeBox styles="width: 100%;" >
                                <Text className="hide-on-hover" color="text-weak">No address set</Text>
                                <EnhancedBox
                                    className="show-on-hover"
                                    styles="width: 100%; position: absolute; left: 0; top: 0;">
                                    <Button
                                        event={{section: 'customer_list', element: 'select_preferred_address', description: datum?.uuid}}
                                        label={(
                                            <Text color="text-weak"><u>Select preferred address</u></Text>
                                        )}
                                        plain
                                        onClick={() => {
                                            setUpdateAddressCustomerId(datum?.id);
                                            setShowSelectAddessModal(true);
                                        }}/>
                                </EnhancedBox>
                            </PositionRelativeBox>
                        </EaseInOnHoverBox>

                    )
            },
            {
                selector: row => row.total_open_balance_amount,
                name: (
                    <Text size="xsmall" textAlign="end">Open balance</Text>
                ),
                sortable: true,
                sortField: 'total_open_balance_amount',
                right: true,
                cell: (datum) => formatCurrency(datum?.total_open_balance_amount, datum?.currency)
            },
            {
                selector: row => row.last_invoiced_at,
                name: (<Text size="xsmall" textAlign="end">Last invoiced</Text>),
                sortable: true,
                sortField: 'last_invoiced_at',
                right: true,
                cell: (datum) => datum?.last_invoiced_at ? (<Text textAlign="end">{sentencecase(formatDistance(new Date(datum?.last_invoiced_at * 1000), new Date()).replace('about ', ''))} ago</Text>) : ''
            },
            {
                selector: row => row.invoice_count,
                name: (
                    <Text size="xsmall" textAlign="end">Invoices sent</Text>
                ),
                sortable: true,
                sortField: 'invoice_count',
                right: true,
                cell: (datum) => datum?.invoice_count || ''
            },
            {
                selector: row => row.quick_pay_enabled,
                name: (
                    <Box direction="row" gap="xsmall">
                        <Text size="xsmall" textAlign="start">
                            QuickPay
                        </Text>
                        <DropButton
                            event={{section: 'customer_list', element: 'quick_pay_help_text'}}
                            plain
                            hoverIndicator
                            dropAlign={{right: 'right', top: 'bottom'}}
                            dropContent={(
                                <Box gap="small" border={{color: "accent"}} width={{max: 'medium'}} pad="small">
                                    <Text weight={600}>
                                        QuickPay lets your customers save a payment method on file for easy future payments.
                                    </Text>
                                    <Text>
                                        With QuickPay, your customers can add a payment method on file, which will automatically be charged when their invoices are due.
                                    </Text>
                                    <Button
                                        event={{section: 'customer_list', element: 'quick_pay_help_text_learn_more'}}
                                        onClick={() => setShowHowToSetupQuickPay(true)} plain label={(<u>See set up instructions</u>)} />
                                    {showHowToSetUpQuickPay && (
                                        <Text>To set up QuickPay, choose the customers you’d like to add on this page, then click “Invite to QuickPay.” Or you can go to the billing details in their customer profile and click “Set up QuickPay.”  </Text>
                                    )}
                                </Box>
                            )}
                            label={<FontAwesomeIcon SelectedIcon={IconQuestion} color="status-info" size="xsmall" />} />
                    </Box>
                ),
                sortable: true,
                sortField: 'quick_pay_enabled',
                right: true,
                cell: (datum) => (
                    <FontAwesomeIcon
                        SelectedIcon={datum?.quick_pay_enabled ? IconCircleCheck : IconCircleMinus}
                        color={datum?.quick_pay_enabled ? 'status-ok' : 'neutral-5'}
                        size="small"
                    />
                )
            },
        ].filter(Boolean);
    }, [isMobile, isGroup]);

    const renderEmptyPageMessage = () => {
        if (!isDataLoaded) return (<Loading />);
        return (
            <Box>
                {isGroup? (
                    <EmptyPageMessage>
                        <Box margin={{ bottom: "medium" }}>
                            <Heading level={2} size={isMobile ? "medium" : "large"} margin={{vertical: 'small'}}>
                                You have no customer groups
                            </Heading>
                            <Box width={{max: 'large'}}>
                                <Text>Group your related customers for easy billing. <br/>You'll be able to see their invoices, group balance, and manage any credits in one place.</Text>
                            </Box>
                        </Box>
                        <DisplayInlineBlockBox>
                            <Button
                                event={{section: 'customer_list_empty_page', element: 'create_first_group'}}
                                primary
                                label='Create your first group'
                                onClick={() => setShowAddGroupDrawer(true)}
                                icon={<FontAwesomeIcon SelectedIcon={IconPlus} size="small" />}
                            />
                        </DisplayInlineBlockBox>
                    </EmptyPageMessage>
                ) : (
                    <EmptyCustomerPageMessage />
                )}
                {showAddGroupDrawer && (
                    <CustomerGroupFormDrawer
                        action={"new"}
                        title={"Create group"}
                        values={
                            selectedCustomers && {
                                company_name: "",
                                customer_ids: selectedCustomers.map((c) => c.id) || [],
                            }
                        }
                        onSuccess={({ id, uuid }) => router.push(`/customers/groups/${uuid || ""}`)}
                        onClose={() => setShowAddGroupDrawer(false)}
                    />
                )}
            </Box>
        )
    }

    const getCustomersCSV = ({callback, email}) => {
        const exportError = {type: 'error', message: 'Unable to download csv data. Try again later.'};
        const exportSuccessMessage = {type: 'success', message: `Your file will be sent to ${email}`};

        fetch(`/api/customers${isGroup ? '/groups' : ''}/csv${email ? `?email=${email}` : ''}`, {
            method: 'GET'
        })
            .then(handleFileDownloadErrors)
            .then((data) => {
                data ?
                    notificationCtx.showNotification(exportSuccessMessage) :
                    // uncomment if needed for testing
                    // downloadBlobAsCsvFile(
                    //     data,
                    //     formatCSVfilename(company?.business_name || company?.legal_name, 'customers', false, true)
                    // ) :
                    notificationCtx.showNotification(exportError);
                callback && callback();
            })
            .catch(e => notificationCtx.showNotification(exportError));
    };

    return (isDataLoaded && hasNoData) ? renderEmptyPageMessage() : (
        <Box>
            {showSendCSVModal && (
                <SendCSVModal
                    onClose={()=>{setShowSendCSVModal(false)}}
                    onSubmit={(email) => {
                        getCustomersCSV({ email: email });
                    }}
                />
            )}
            {showSelectAddressModal && updateAddressCustomerId && (
                <SelectAddressModal
                    title="Select preferred billing address"
                    recipientId={updateAddressCustomerId}
                    onSubmit={async (address) => {
                        if (!address) return;
                        // make call to update default billing address
                        await fetch(`/api/customers/${updateAddressCustomerId}`, {
                            method: 'PUT',
                            headers: {
                                'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({
                                billing_address_id: address.id
                            }),
                        }).then(handleErrors);
                        setShowSelectAddessModal(false);
                        mutate(dataTableQueryUrl);
                    }}
                    hideDescription
                    onClose={() => setShowSelectAddessModal(false)}
                    type={"billing"}
                />
            )}
            <DataTableWithBackendSearch
                onExportCSVClicked={()=>{setShowSendCSVModal(true)}}
                onRowClicked={row => {
                    router.push(`/customers/${isGroup ? 'groups/' : ''}${row?.uuid}`)
                }}
                onDataChange={data => {
                    !isDataLoaded && data && setHasNoData(!data.length);
                    setIsDataLoaded(true);
                }}
                columns={columns}
                setQueryUrlCallback={setDataTableQueryUrl}
                resetSelectedRowsRef={resetSelectedRowsRef}
                emptyListMessage={`No ${isGroup ? 'groups' : 'customers'} found`}
                title={title}
                dataName={dataName || ''}
                hideDateRangeFilter
                hideAmountRangeFilter
                hideSearch={isMobile}
                rowName={isGroup ? {plural: 'groups', single: 'group'} : {plural: 'customer', single: 'customer'}}
                reshape={(data) => {
                    return isGroup ? data?.customer_groups : data?.customers;
                }}
                getUrl={({filterQuery, ...rest}) => {
                    return getQueryString({
                        ...rest,
                        baseUrl: `api/customers${isGroup ? '/groups' : ''}`,
                    });
                }}
                dataTableWrapperProps={dataTableWrapperProps}
                selectableRows
                controls={totalCount ? (
                    <Box pad={{horizontal: 'xsmall'}}>
                        <Text color="text-weak">{totalCount} {isGroup ? `group` : 'customer'}{totalCount > 1 ? 's' : ''}</Text>
                    </Box>
                ) : ''}
                selectedControls={(
                    <CustomerListSelectedControls
                        selectedCustomers={selectedCustomers}
                        isGroup={isGroup}
                        reload={() => {
                            mutate(dataTableQueryUrl);
                            resetSelectedRowsRef.current();
                        }}
                        setShowAddGroupDrawer={setShowAddGroupDrawer}/>)}
                onSelectedRowsChange={(data) => data?.selectedRows && setSelectedCustomers(data.selectedRows)}
                {...rest}
            />
            {showAddGroupDrawer && (
                <CustomerGroupFormDrawer
                    action={"new"}
                    title={"Create group"}
                    values={
                        selectedCustomers && {
                            company_name: "",
                            customer_ids: selectedCustomers.map((c) => c.id) || [],
                        }
                    }
                    onSuccess={({ id, uuid }) => router.push(`/customers/groups/${uuid || ""}`)}
                    onClose={() => setShowAddGroupDrawer(false)}
                />
            )}
        </Box>
    );
};

export default CustomerList;
